import { useExamEventDetails } from '@exam-center/exam-event-management';
import {
  type ExamEventExamineeStatus,
  type ExamineeRegistrationStatus,
} from '@exam-center/graphql';
import { APP_PERMISSIONS } from '@exam-center/permission';
import {
  HistoryIcon,
  MoreMenuIconButton,
  NoAccountsOutlinedIcon,
  PersonOutlinedIcon,
} from '@shared/components';
import { useDisclosure } from '@shared/hooks';
import { usePermission } from '@shared/permissions';
import { useMemo } from 'react';

import {
  EXAM_EVENT_ACTIONABLE_STATUSES,
  EXAM_EVENT_EXAMINEE_CANCELLABLE_STATUSES,
  EXAM_EVENT_EXAMINEE_OVERRIDABLE_EMAIL_STATUSES,
  EXAM_EVENT_EXAMINEE_REINSTATABLE_STATUSES,
} from '../../constants';
import { getReinstatedStatus } from '../../utils';
import { CancelExamEventExamineeModal } from '../cancel-exam-event-examinee-modal';
import { OverrideExamEventExamineeEmailModal } from '../override-exam-event-examinee-email-modal';
import { ReinstateExamEventExamineeModal } from '../reinstate-exam-event-examinee-modal';

interface ExamineeActionMenuProps {
  examEventExamineeId: string;
  examEventExamineeStatus: ExamEventExamineeStatus;
  examCourseCode: string;
  examEventExamineeRegistrationStatus: ExamineeRegistrationStatus;
  examEventSessionExamineesExamStartedAt: (Date | string | null)[];
}

export default function ExamineeActionMenu(props: Readonly<ExamineeActionMenuProps>) {
  const {
    examEventExamineeId,
    examEventExamineeStatus,
    examCourseCode,
    examEventExamineeRegistrationStatus,
    examEventSessionExamineesExamStartedAt,
  } = props;

  const {
    examEvent: { id: examEventId, status: examEventStatus },
  } = useExamEventDetails();
  const { permissions } = usePermission();

  const [
    isOverrideEmailModalOpen,
    { close: closeOverrideEmailModal, open: openOverrideEmailModal },
  ] = useDisclosure(false);

  const [isCancelModalOpen, { close: closeCancelModal, open: openCancelModal }] =
    useDisclosure(false);

  const [isReinstateModalOpen, { close: closeReinstateModal, open: openReinstateModal }] =
    useDisclosure(false);

  const baseOptions = useMemo(
    () => [
      {
        icon: <PersonOutlinedIcon sx={{ color: (theme) => theme.palette.grey[600] }} />,
        label: 'แก้ไขอีเมลผู้เข้าสอบ',
        onClick: openOverrideEmailModal,
        requiredPermission: [APP_PERMISSIONS.EXAM_EVENT_MANAGEMENT.EXAMINEES.UPDATE],
        shouldDisplay:
          EXAM_EVENT_EXAMINEE_OVERRIDABLE_EMAIL_STATUSES.includes(examEventExamineeStatus),
      },
      {
        icon: <NoAccountsOutlinedIcon sx={{ color: (theme) => theme.palette.error.main }} />,
        label: 'ยกเลิกผู้เข้าสอบ',
        onClick: openCancelModal,
        requiredPermission: [APP_PERMISSIONS.EXAM_EVENT_MANAGEMENT.EXAMINEES.UPDATE],
        shouldDisplay:
          EXAM_EVENT_EXAMINEE_CANCELLABLE_STATUSES.includes(examEventExamineeStatus) &&
          EXAM_EVENT_ACTIONABLE_STATUSES.includes(examEventStatus),
        sx: { color: 'error.main', '&:hover': { bgcolor: 'primary.lighter' } },
      },
      {
        icon: <HistoryIcon sx={{ color: (theme) => theme.palette.grey[600] }} />,
        label: 'คืนสถานะผู้เข้าสอบ',
        onClick: openReinstateModal,
        requiredPermission: [APP_PERMISSIONS.EXAM_EVENT_MANAGEMENT.EXAMINEES.UPDATE],
        shouldDisplay:
          EXAM_EVENT_EXAMINEE_REINSTATABLE_STATUSES.includes(examEventExamineeStatus) &&
          EXAM_EVENT_ACTIONABLE_STATUSES.includes(examEventStatus) &&
          getReinstatedStatus(
            examEventExamineeRegistrationStatus,
            examEventSessionExamineesExamStartedAt,
          ) !== null,
      },
    ],
    [
      openOverrideEmailModal,
      openCancelModal,
      openReinstateModal,
      examEventStatus,
      examEventExamineeStatus,
      examEventExamineeRegistrationStatus,
      examEventSessionExamineesExamStartedAt,
    ],
  );

  const options = useMemo(
    () =>
      baseOptions.filter(
        (option) =>
          option.shouldDisplay &&
          option.requiredPermission.every((permission) => permissions.includes(permission)),
      ),
    [baseOptions, permissions],
  );

  if (options.length === 0) {
    return null;
  }

  return (
    <>
      <MoreMenuIconButton options={options} />
      <OverrideExamEventExamineeEmailModal
        open={isOverrideEmailModalOpen}
        onClose={closeOverrideEmailModal}
        examEventId={examEventId}
        examEventExamineeId={examEventExamineeId}
      />
      <CancelExamEventExamineeModal
        open={isCancelModalOpen}
        onClose={closeCancelModal}
        examEventExamineeId={examEventExamineeId}
        examCourseCode={examCourseCode}
      />
      <ReinstateExamEventExamineeModal
        open={isReinstateModalOpen}
        onClose={closeReinstateModal}
        examEventExamineeId={examEventExamineeId}
        examCourseCode={examCourseCode}
      />
    </>
  );
}
